import { Badge, Button, Checkbox, Select } from '@cloudflare/kumo';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppToast } from '../hooks/useAppToast';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { DangerButton } from '../components/ui/DangerButton';
import { SearchField } from '../components/ui/SearchField';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';
import {
  interactiveCampaignApi,
  InteractiveCampaign,
  Connection,
} from '../services/interactiveCampaignApi';

type ConfirmAction = { id: string; name: string; action: 'delete' | 'publish' | 'resume' | 'complete' } | null;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos os Status' },
  { value: 'DRAFT', label: 'Rascunho' },
  { value: 'SCHEDULED', label: 'Agendada' },
  { value: 'STARTED', label: 'Iniciada' },
  { value: 'PAUSED', label: 'Pausada' },
  { value: 'COMPLETED', label: 'Finalizada' },
];

export function InteractiveCampaignPage() {
  const toast = useAppToast();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<InteractiveCampaign[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<InteractiveCampaign[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  useEffect(() => {
    loadData();
    const intervalId = setInterval(loadData, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignsData, connectionsData] = await Promise.all([
        interactiveCampaignApi.getCampaigns(),
        interactiveCampaignApi.getConnections(),
      ]);
      setAllCampaigns(campaignsData);
      setCampaigns(campaignsData);
      setConnections(connectionsData);
    } catch (error: any) {
      toast.error(`Erro ao carregar dados: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = allCampaigns;
    if (searchTerm) {
      filtered = filtered.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    setCampaigns(filtered);
  }, [searchTerm, statusFilter, allCampaigns]);

  const handleEditCampaign = (id: string) => navigate(`/campanhas/interativa/${id}`);

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    const { id, name, action } = confirmAction;
    setConfirmAction(null);
    try {
      if (action === 'delete') {
        setDeletingId(id);
        await interactiveCampaignApi.deleteCampaign(id);
        toast.success('Campanha excluída com sucesso!');
        await loadData();
        setDeletingId(null);
      } else if (action === 'publish') {
        await interactiveCampaignApi.publishCampaign(id);
        toast.success('Campanha publicada!');
        await loadData();
      } else if (action === 'resume') {
        await interactiveCampaignApi.resumeCampaign(id);
        toast.success(`Campanha "${name}" retomada!`);
        await loadData();
      } else if (action === 'complete') {
        await interactiveCampaignApi.completeCampaign(id);
        toast.success(`Campanha "${name}" finalizada!`);
        await loadData();
      }
    } catch (error: any) {
      toast.error(`Erro: ${error.message}`);
      setDeletingId(null);
    }
  };

  const handlePauseCampaign = async (id: string, name: string) => {
    try {
      await interactiveCampaignApi.pauseCampaign(id);
      toast.success(`Campanha "${name}" pausada!`);
      await loadData();
    } catch (error: any) {
      toast.error(`Erro ao pausar campanha: ${error.message}`);
    }
  };

  const handleDuplicateCampaign = async (id: string, name: string) => {
    try {
      const duplicated = await interactiveCampaignApi.duplicateCampaign(id);
      toast.success(`Campanha "${name}" duplicada com sucesso!`);
      await loadData();
      navigate(`/campanhas/interativa/${duplicated.id}`);
    } catch (error: any) {
      toast.error(`Erro ao duplicar campanha: ${error.message}`);
    }
  };

  const handleToggleSelectCampaign = (campaignId: string) => {
    setSelectedCampaignIds(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleSelectAllCampaigns = () => {
    if (selectedCampaignIds.length === campaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(campaigns.map(c => c.id));
    }
  };

  const executeBulkDelete = async () => {
    setConfirmBulkDelete(false);
    try {
      setBulkDeleting(true);
      await Promise.all(selectedCampaignIds.map(id => interactiveCampaignApi.deleteCampaign(id)));
      toast.success(`${selectedCampaignIds.length} campanha(s) excluída(s) com sucesso!`);
      setSelectedCampaignIds([]);
      await loadData();
    } catch (error: any) {
      toast.error(`Erro ao excluir campanhas: ${error.message}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'STARTED': return <Badge variant="green">Iniciada</Badge>;
      case 'SCHEDULED': return <Badge variant="blue">Aguardando</Badge>;
      case 'PAUSED': return <Badge variant="orange">Pausada</Badge>;
      case 'COMPLETED': return <Badge variant="neutral">Finalizada</Badge>;
      default: return <Badge variant="neutral">Rascunho</Badge>;
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const getTriggerConnections = (campaign: any) => {
    const graph = campaign.graph;
    if (!graph?.nodes) return [];
    const triggerNode = graph.nodes.find((n: any) => n.data?.nodeType === 'trigger');
    if (!triggerNode?.data?.config) return [];
    return triggerNode.data.config.connections || [];
  };

  return (
    <div className="min-h-screen bg-[var(--surface-2)]">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Campanhas Interativas</h2>
          <div style={{ color: 'var(--text-muted)' }}>
            {selectedCampaignIds.length > 0
              ? `${selectedCampaignIds.length} campanha(s) selecionada(s)`
              : `${allCampaigns.length} campanhas cadastradas`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedCampaignIds.length > 0 ? (
            <>
              <DangerButton
                onClick={() => setConfirmBulkDelete(true)}
                disabled={bulkDeleting}
                loading={bulkDeleting}
              >
                Excluir Selecionadas ({selectedCampaignIds.length})
              </DangerButton>
              <Button variant="secondary" onClick={() => setSelectedCampaignIds([])}>
                Cancelar Seleção
              </Button>
            </>
          ) : (
            <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate('/campanhas/interativa/nova')}>
              Nova Campanha
            </Button>
          )}
        </div>
      </div>

      <div className="page-content space-y-6">
        <div className="bg-white rounded-lg border border-[var(--border)] p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <SearchField
                ariaLabel="Buscar campanhas"
                placeholder="Buscar campanhas por nome..."
                value={searchTerm}
                onChange={setSearchTerm}
              />
            </div>
            <div className="w-full md:w-48">
              <Select value={(STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? null)?.value ?? null} items={STATUS_OPTIONS} onValueChange={(value: string | null) => {
                  const v = value;
                  if (v) setStatusFilter(v);
                }} aria-label="Status" placeholder="Todos os Status" />
            </div>
            {(searchTerm || statusFilter !== 'all') && (
              <Button variant="secondary" onClick={handleClearFilters}>
                Limpar Filtros
              </Button>
            )}
          </div>
        </div>

        {campaigns.length > 0 && (
          <div className="flex items-center justify-between">
            <button
              onClick={handleSelectAllCampaigns}
              className="text-sm text-[var(--link)] hover:underline font-medium"
            >
              {selectedCampaignIds.length === campaigns.length ? 'Desmarcar todas' : 'Selecionar todas'}
            </button>
            {selectedCampaignIds.length > 0 && (
              <div className="!text-[var(--text-muted)]">
                {selectedCampaignIds.length} de {campaigns.length} selecionadas
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg border border-[var(--border)]">
          {loading ? (
            <div className="py-8"><SkeletonText lines={4} /></div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma campanha interativa</h3>
              <p className="mt-1 text-sm text-gray-500">Comece criando sua primeira campanha interativa.</p>
              <div className="mt-6">
                <Button variant="primary" onClick={() => navigate('/campanhas/interativa/nova')}>
                  Nova Campanha
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 w-12">
                      <Checkbox onCheckedChange={handleSelectAllCampaigns} aria-label="Selecionar todas" checked={selectedCampaignIds.length === campaigns.length && campaigns.length > 0} indeterminate={selectedCampaignIds.length > 0 && selectedCampaignIds.length < campaigns.length} />
                    </th>
                    <th className="px-6 py-3 text-left lg-th">Nome</th>
                    <th className="px-6 py-3 text-left lg-th">Conexões</th>
                    <th className="px-6 py-3 text-left lg-th">Status</th>
                    <th className="px-6 py-3 text-left lg-th">Criada em</th>
                    <th className="px-6 py-3 text-right lg-th">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <Checkbox onCheckedChange={() => handleToggleSelectCampaign(campaign.id)} aria-label={`Selecionar ${campaign.name}`} checked={selectedCampaignIds.includes(campaign.id)} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-[var(--border)] rounded-xl">
                            <svg className="h-6 w-6 text-[var(--brand-text)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                            <div className="text-xs text-gray-500">{campaign.graph?.nodes?.length || 0} nodes</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                        {(() => {
                          const ids = getTriggerConnections(campaign);
                          if (ids.length === 0) return <div className="text-sm text-gray-500">-</div>;
                          const conns = connections.filter(c => ids.includes(c.id));
                          if (conns.length === 0) return <div className="text-sm text-gray-500">-</div>;
                          return (
                            <div>
                              <div className="text-sm text-gray-900">{conns[0].instanceName}</div>
                              {conns.length > 1 && (
                                <div className="text-xs text-gray-500">+{conns.length - 1} outra{conns.length > 2 ? 's' : ''}</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                        {getStatusBadge(campaign.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                        {formatDate(campaign.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end space-x-1">
                          {campaign.status === 'DRAFT' && (
                            <Button variant="ghost" shape="square" icon={ICONS.Checkmark} aria-label="Publicar" onClick={() => setConfirmAction({ id: campaign.id, name: campaign.name, action: 'publish' })} />
                          )}
                          {(campaign.status === 'STARTED' || campaign.status === 'SCHEDULED') && (
                            <Button variant="ghost" shape="square" icon={ICONS.Pause} aria-label="Pausar" onClick={() => handlePauseCampaign(campaign.id, campaign.name)} />
                          )}
                          {campaign.status === 'PAUSED' && (
                            <Button variant="ghost" shape="square" icon={ICONS.Play} aria-label="Retomar" onClick={() => setConfirmAction({ id: campaign.id, name: campaign.name, action: 'resume' })} />
                          )}
                          {(campaign.status === 'STARTED' || campaign.status === 'SCHEDULED' || campaign.status === 'PAUSED') && (
                            <Button variant="ghost" shape="square" icon={ICONS.CheckmarkWithCircle} aria-label="Finalizar" onClick={() => setConfirmAction({ id: campaign.id, name: campaign.name, action: 'complete' })} />
                          )}
                          <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label="Editar" onClick={() => handleEditCampaign(campaign.id)} />
                          <Button variant="ghost" shape="square" icon={ICONS.Charts} aria-label="Ver Relatório" onClick={() => navigate(`/campanhas/interativa/${campaign.id}/relatorio`)} />
                          <Button variant="ghost" shape="square" icon={ICONS.Copy} aria-label="Duplicar" onClick={() => handleDuplicateCampaign(campaign.id, campaign.name)} />
                          <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label="Excluir" disabled={deletingId === campaign.id} onClick={() => setConfirmAction({ id: campaign.id, name: campaign.name, action: 'delete' })} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Exclusão: digitar DELETE ou o nome da campanha */}
      <DangerConfirmModal
        open={confirmAction?.action === 'delete'}
        title={`Excluir "${confirmAction?.name ?? ''}"?`}
        confirmText="Excluir"
        objectName={confirmAction?.name}
        onConfirm={executeConfirmAction}
        onCancel={() => setConfirmAction(null)}
      >
        A campanha, o fluxo e as conversas em andamento são excluídos. Não dá para desfazer.
      </DangerConfirmModal>

      <ConfirmDialog
        open={confirmAction !== null && confirmAction.action !== 'delete'}
        title={
          confirmAction?.action === 'delete'  ? `Excluir "${confirmAction?.name}"?` :
          confirmAction?.action === 'publish' ? `Publicar "${confirmAction?.name}"?` :
          confirmAction?.action === 'resume'  ? `Retomar "${confirmAction?.name}"?` :
          `Finalizar "${confirmAction?.name}"?`
        }
        confirmText={
          confirmAction?.action === 'delete'  ? 'Excluir' :
          confirmAction?.action === 'publish' ? 'Publicar' :
          confirmAction?.action === 'resume'  ? 'Retomar' : 'Finalizar'
        }
        variant={confirmAction?.action === 'delete' ? 'danger' : 'primary'}
        onConfirm={executeConfirmAction}
        onCancel={() => setConfirmAction(null)}
      >
        {confirmAction?.action === 'delete'
          ? 'Esta ação não pode ser desfeita.'
          : confirmAction?.action === 'publish'
          ? 'A campanha começará a processar mensagens imediatamente.'
          : 'Esta ação não pode ser desfeita. A campanha será encerrada.'}
      </ConfirmDialog>

      <DangerConfirmModal
        open={confirmBulkDelete}
        title={`Excluir ${selectedCampaignIds.length} campanha(s)?`}
        confirmText="Excluir todas"
        onConfirm={executeBulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
      >
        As campanhas selecionadas e os fluxos delas são excluídos. Não dá para desfazer.
      </DangerConfirmModal>
    </div>
  );
}
